import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function formatCurrency(value: number): string {
	// Format with abbreviations for large numbers
	if (value >= 1_000_000_000) {
		return `KSh ${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
	}
	
	if (value >= 1_000_000) {
		return `KSh ${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
	}
	
	if (value >= 1_000) {
		return `KSh ${value.toLocaleString()}`;
	}
	
	// For smaller numbers
	return `KSh ${value.toLocaleString()}`;
}

export function formatPrice(price: number | string): string {
	if (price === null || price === undefined) return "N/A";
	
	const numericPrice = typeof price === "string" ? parseFloat(price) : price;
	
	if (isNaN(numericPrice)) return "N/A";
	
	return numericPrice.toLocaleString("en-KE");
}

export function formatDate(date: Date | string | number): string {
	if (!date) return "N/A";
	
	const dateObject = date instanceof Date ? date : new Date(date);
	
	// Check if the date is valid
	if (isNaN(dateObject.getTime())) return "Invalid date";
	
	// Format the date: DD MMM YYYY (e.g., 15 Jan 2023)
	return dateObject.toLocaleDateString('en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric'
	});
}

export function getInitials(name: string): string {
	if (!name) return "";

	// If it's an email, use the first part
	if (name.includes("@")) {
		name = name.split("@")[0];
	}

	const parts = name.split(/[ ._-]/);
	let initials = "";

	for (let i = 0; i < Math.min(parts.length, 2); i++) {
		if (parts[i][0]) {
			initials += parts[i][0].toUpperCase();
		}
	}

	return initials;
}

export const getCarName = (car: any) => {
	const make = car.car_make?.name || "Unknown Make";
	const model = car.car_model || "Unknown Model";
	return `${make} ${model}`;
};

export function formattedPrice(price: number | null | undefined): string {
	if (price === null || price === undefined) {
		return "Contact Us";
	}
	
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'KES',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(price);
}

export interface PaginationOptions {
	page?: number;
	limit?: number;
}

export function getPaginationData(options: PaginationOptions) {
	const page = Math.max(1, options.page || 1);
	const limit = Math.max(1, options.limit || 10);
	const offset = (page - 1) * limit;
	
	return { page, limit, offset };
}
